/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { AttributeValue, ProductsDataActions, SimpleProduct, StoreOperationsDataActions } from '@msdyn365-commerce/retail-proxy';
import {
    getAttributeValuesByConfiguration,
    getSelectedProductIdFromActionInput,
    getSelectedVariant,
    SelectedVariantInput
} from '@msdyn365-commerce-modules/retail-actions';

/**
 * Chains getting the SelectedVariant with GetActivePrice.
 */
export class AttributesForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public catalogId: number;

    public selectedProduct: SimpleProduct | undefined;

    constructor(productId: number, channelId: number, selectedProduct?: SimpleProduct, catalogId?: number) {
        this.productId = productId;
        this.channelId = channelId;
        this.selectedProduct = selectedProduct;
        this.catalogId = catalogId || 0;
    }

    public getCacheKey = () => 'AttributesForSelectedVariant';

    public getCacheObjectType = () => 'ProductAttributeValue';

    public dataCacheType = (): CacheType => 'none';
}

export const createAttributesForSelectedVariantInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): AttributesForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    const catalogId = getCatalogId(inputData.requestContext);

    if (productId) {
        return new AttributesForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId, undefined, catalogId);
    }
    throw new Error('Unable to create PriceForSelectedVariantInput, no productId found on module config or query');
};

/**
 * Calls the Retail API a get a list of dimension values for a given product.
 * @param input - The input.
 * @param context - The context.
 * @returns The attribute values.
 */
export async function getAttributesForSelectedVariantAction(
    input: AttributesForSelectedVariantInput,
    context: IActionContext
): Promise<AttributeValue[]> {
    return Promise.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(
                    input.productId,
                    input.channelId,
                    undefined,
                    undefined,
                    context.requestContext
                );

                return getSelectedVariant(selectedVariantInput, context);
            }

            return activeProduct;
        })
        .then<AttributeValue[] | null>((productResult: SimpleProduct | null) => {
            const activeProduct: SimpleProduct | undefined = <SimpleProduct | undefined>productResult;

            if (activeProduct) {
                return ProductsDataActions.getAttributeValuesAsync(
                    {
                        callerContext: context
                    },
                    activeProduct.RecordId,
                    input.channelId,
                    input.catalogId
                );
            }

            return null;
        })
        .then(async (attributeValues: AttributeValue[] | null) => {
            const channelInventoryConfiguration = await StoreOperationsDataActions.getInventoryConfigurationAsync({
                callerContext: context
            });

            return { attributeValues, channelInventoryConfiguration };
        })
        .then(result => {
            return getAttributeValuesByConfiguration(result.attributeValues, result.channelInventoryConfiguration);
        })
        .catch((error: Error) => {
            context.trace(error.message);
            throw new Error('[getPriceForSelectedVariantAction]Error executing action');
        });
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/product-info/get-attributes-for-selected-variant',
    action: <IAction<AttributeValue[]>>getAttributesForSelectedVariantAction,
    input: createAttributesForSelectedVariantInput
});
